'use client'

import type { SearchProps as IndexSearchProps } from '.'

import { Input, InputSize } from '@moment/design-system-ui/input'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import {
  type ClipboardEventHandler,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDebouncedCallback } from 'use-debounce'

import Button, { ButtonVariant } from '@/components/atomic/button'
import { ButtonSize } from '@/components/atomic/button/variants'
import Icon from '@/components/atomic/icon'
import { IconName } from '@/components/atomic/icon/types'
import { BodySubtext } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

interface BaseSearchProps
  extends Required<Omit<IndexSearchProps, 'onClear' | 'onPaste' | 'disabled'>> {
  onClear?: () => void
  onPaste?: ClipboardEventHandler<HTMLInputElement | undefined>
  disabled?: boolean
}

type AutocompleteResultsProps = {
  onResultClick: (label: string) => void
  results: string[]
}

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 w-72 rounded-md border bg-background text-foreground p-4 shadow-md outline-hidden',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

const Search = forwardRef<HTMLInputElement, BaseSearchProps>(
  (
    {
      debounceDelay,
      includeIcon,
      minInputLengthForAutocomplete,
      onInputChange: inputChangeFn,
      onSubmit: submitFn,
      placeholder,
      showAutocomplete,
      onClear,
      onPaste,
      disabled,
      value: initialValue,
    },
    _ref,
  ) => {
    const [input, setInput] = useState<string>(initialValue ?? '') // What is typed into the input
    const [queryTerm, setQueryTerm] = useState<string>(initialValue ?? '') // What is passed to the inputChangeFn
    const [showResults, setShowResults] = useState<boolean>(false)
    const [autocompleteResults, setAutocompleteResults] = useState<string[]>([])
    const [focusedIndex, setFocusedIndex] = useState<number>(-1)
    const inputRef = useRef<HTMLInputElement>(null)
    const resultsRef = useRef<HTMLUListElement>(null)

    useEffect(() => {
      setInput(initialValue ?? '')
      setQueryTerm(initialValue ?? '')
    }, [initialValue])

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInput(value)
        if (value.length >= minInputLengthForAutocomplete) {
          setQueryTerm(value)
        } else {
          setQueryTerm('')
          setAutocompleteResults([])
          setShowResults(false)
        }
      },
      [minInputLengthForAutocomplete],
    )

    const debouncedOnInputChange = useDebouncedCallback((term: string) => {
      const result = inputChangeFn(term)
      // Only set autocomplete results if showAutocomplete is true
      if (showAutocomplete) {
        Promise.resolve(result).then((results) => {
          if (Array.isArray(results)) {
            setAutocompleteResults(results)
            setShowResults(true)
          }
        })
      }
    }, debounceDelay)

    useEffect(() => {
      debouncedOnInputChange(queryTerm)
    }, [queryTerm, debouncedOnInputChange])

    const handleSubmit = useCallback(
      (label: string) => {
        setInput(label)
        submitFn(label)
        setShowResults(false)
        setAutocompleteResults([])
      },
      [submitFn],
    )

    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement | HTMLLIElement>,
    ) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (focusedIndex < autocompleteResults.length - 1) {
          setFocusedIndex((prev) => prev + 1)
        } else if (focusedIndex === -1 && autocompleteResults.length > 0) {
          setFocusedIndex(0)
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (focusedIndex > 0) {
          setFocusedIndex((prev) => prev - 1)
        } else if (focusedIndex === 0) {
          setFocusedIndex(-1)
        }
      } else if (e.key === 'Enter') {
        if (focusedIndex >= 0) {
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          handleSubmit(autocompleteResults[focusedIndex]!)
        } else {
          handleSubmit(input)
        }
      }
    }

    const handleClear = () => {
      setInput('')
      setQueryTerm('')
      setAutocompleteResults([])
      setShowResults(false)
      inputRef.current?.focus()

      if (onClear && !disabled) {
        onClear()
      }
    }

    useEffect(() => {
      // This is a temporary fix to ensure the input is not automatically focused on page load
      // TODO - We will need to refactor this when we have a use case for showing a dropdown of results
      // because the input will still autofocus in that case.
      if (showAutocomplete) {
        if (focusedIndex === -1) {
          inputRef.current?.focus()
        } else {
          const focusedElement = resultsRef.current?.children[
            focusedIndex
          ] as HTMLElement
          focusedElement?.focus()
        }
      }
    }, [focusedIndex, showAutocomplete])

    const AutocompleteResults: React.FC<AutocompleteResultsProps> = ({
      results,
      onResultClick,
    }) => (
      <ul
        ref={resultsRef}
        className="list-none p-1 m-0 max-w-full overflow-hidden rounded-md border-none"
      >
        {results.map((result: string, index: number) => (
          <li
            key={`search-item-${index}`}
            className={cn(
              'py-2 px-3 cursor-pointer truncate outline-hidden rounded-sm',
              'hover:bg-moment-hover',
              focusedIndex === index ? 'bg-moment-hover' : '',
              'focus:bg-moment-hover',
            )}
            onClick={() => onResultClick(result)}
            onMouseEnter={() => setFocusedIndex(index)}
            onKeyDown={handleKeyDown}
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Bulk suppression as part of biome migration
            role="option"
            tabIndex={focusedIndex === index ? 0 : -1}
            aria-selected={focusedIndex === index}
          >
            <BodySubtext>{result}</BodySubtext>
          </li>
        ))}
      </ul>
    )

    const preventDefaultHandler: (e: Event) => void = (e: Event) =>
      e.preventDefault()

    const leftSection = includeIcon ? (
      <Icon
        iconName={IconName.MagnifyingGlassIcon}
        className="text-moment-blue-grey-400 dark:text-moment-grey-400"
      />
    ) : undefined

    return (
      <div className="relative w-full">
        <div className="w-full relative">
          <Input
            ref={inputRef}
            size={InputSize.Medium}
            type="text"
            placeholder={placeholder}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            leftSection={leftSection}
            onPaste={onPaste}
            disabled={disabled}
          />

          {input && (
            <Button
              className={cn(
                'absolute right-0 top-1/2 transform -translate-y-1/2 border-none bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent',
                disabled && 'pointer-events-none',
              )}
              variant={ButtonVariant.Secondary}
              size={ButtonSize.Small}
              onClick={handleClear}
              icon={<Icon iconName={IconName.XMarkIcon} className="w-54 h-5" />}
            />
          )}
        </div>
        {showAutocomplete && (
          <Popover open={showResults} onOpenChange={() => {}}>
            <PopoverTrigger asChild>
              <div
                aria-hidden="true"
                className={`w-[var(--radix-popper-anchor-width)] h-0 overflow-hidden`}
              />
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popper-anchor-width)] p-0 bg-background"
              align="start"
              avoidCollisions={false}
              sideOffset={0}
              onOpenAutoFocus={preventDefaultHandler}
              onCloseAutoFocus={preventDefaultHandler}
            >
              <AutocompleteResults
                results={autocompleteResults}
                onResultClick={handleSubmit}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    )
  },
)

Search.displayName = 'Search'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default Search
